import ScButton from "@/src/components/ui-kit/button";
import { UserRole, UserStatus } from "@/src/models/user.model";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle } from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, View } from "react-native";
import { RadioButton } from "react-native-paper";

type UserFilterModalProps = Readonly<{
  visible: boolean;
  onClose: () => void;
  onApply: (filters: {
    role: UserRole[] | null;
    status: UserStatus | null;
  }) => void;
  hideStatus?: boolean;
}>;
const UserFilterModal = ({
  visible,
  onClose,
  onApply,
  hideStatus,
}: UserFilterModalProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [status, setStatus] = useState<UserStatus | null>(null);

  const handleClearAll = () => {
    setSelectedRole(null);
    setStatus(null);
  };

  const handleApply = () => {
    onApply({
      role: selectedRole ? [selectedRole] : null,
      status,
    });
  };

  if (!visible) return null;

  return (
    <Modal transparent animationType="fade">
      <Pressable
        style={varStyle}
        className="flex-1 bg-shadow-dark justify-center items-center px-6"
        onPress={onClose}
      >
        <Pressable
          className="bg-ash rounded-2xl w-full max-w-sm md:w-auto md:max-w-md"
          onPress={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <View className="flex-row items-center justify-between px-6 py-3">
            <Text className="font-medium text-onyx">
              {t("LABEL_SELECT_FILTERS")}
            </Text>
            <Pressable
              className="p-1.5 rounded-full hover:bg-ash-3"
              onPress={onClose}
            >
              <CloseCircle color={themeVars["--color-primary-default"]} />
            </Pressable>
          </View>

          {/* Status */}
          {!hideStatus && (
            <View className="gap-1 md:border-t md:border-ash-3 px-6 py-3">
              <Text className="font-medium text-onyx mb-1">
                {t("LABEL_STATUS")}
              </Text>
              <RadioButton.Group
                onValueChange={(value) => setStatus(value as UserStatus)}
                value={status ?? ""}
              >
                <Pressable
                  className="flex-row items-center py-1 active:opacity-70 hover:bg-ash-3 hover:rounded-lg"
                  onPress={() => setStatus("active" as UserStatus)}
                >
                  <RadioButton
                    value="active"
                    color={themeVars["--color-primary-default"]}
                  />
                  <Text className="ml-2 text-slate">{t("LABEL_ACTIVE")}</Text>
                </Pressable>
                <Pressable
                  className="flex-row items-center py-1 active:opacity-70 hover:bg-ash-3 hover:rounded-lg"
                  onPress={() => setStatus("inactive" as UserStatus)}
                >
                  <RadioButton
                    value="inactive"
                    color={themeVars["--color-primary-default"]}
                  />
                  <Text className="ml-2 text-slate">{t("LABEL_INACTIVE")}</Text>
                </Pressable>
              </RadioButton.Group>
            </View>
          )}

          {/* Role */}
          <View className="md:border-t md:border-ash-3 gap-1 px-6 py-3">
            <Text className="font-medium text-onyx mb-1">
              {t("LABEL_ROLE")}
            </Text>
            <RadioButton.Group
              onValueChange={(value) => setSelectedRole(value as UserRole)}
              value={selectedRole ?? ""}
            >
              <Pressable
                className="flex-row items-center py-1 active:opacity-70 hover:bg-ash-3 hover:rounded-lg"
                onPress={() => setSelectedRole(UserRole.SUPER_ADMIN)}
              >
                <RadioButton
                  value={UserRole.SUPER_ADMIN}
                  color={themeVars["--color-primary-default"]}
                />
                <Text className="ml-2 text-slate">
                  {t("LABEL_SUPER_ADMIN")}
                </Text>
              </Pressable>
              <Pressable
                className="flex-row items-center py-1 active:opacity-70 hover:bg-ash-3 hover:rounded-lg"
                onPress={() => setSelectedRole(UserRole.ADMIN)}
              >
                <RadioButton
                  value={UserRole.ADMIN}
                  color={themeVars["--color-primary-default"]}
                />
                <Text className="ml-2 text-slate">{t("LABEL_ADMIN")}</Text>
              </Pressable>
              <Pressable
                className="flex-row items-center py-1 active:opacity-70 hover:bg-ash-3 hover:rounded-lg"
                onPress={() => setSelectedRole(UserRole.MEMBER)}
              >
                <RadioButton
                  value={UserRole.MEMBER}
                  color={themeVars["--color-primary-default"]}
                />
                <Text className="ml-2 text-slate">{t("LABEL_MEMBER")}</Text>
              </Pressable>
            </RadioButton.Group>
          </View>

          {/* Actions */}
          <View className="flex-row justify-between md:border-t md:border-ash-3 items-center gap-3 px-3 py-3">
            <ScButton
              text="BTN_CLEAR_ALL"
              mode="text"
              onPress={handleClearAll}
              textColor={themeVars["--color-primary-default"]}
            />
            <View className="flex-row gap-3">
              <ScButton text="BTN_CANCEL" mode="outlined" onPress={onClose} />
              <ScButton text="BTN_APPLY" onPress={handleApply} />
            </View>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};
export default UserFilterModal;
